import { store } from './store'
import { browser_local_storage, is_local_storage_event } from './browser-storage'

// Adaptado do MM: packages/ui/src/tema-leitura/preferencia.js e
// packages/ui/src/barra/navegador/tema.js, revisão a4ea5c473.
// Preserva a chave do NaBomba e usa seus tokens Bootstrap e store.
type ThemePreference = 'system' | 'light' | 'dark'
const storage_key = 'theme-preference'
const options = {
  system: { label: 'Automático', icon: 'desktop' },
  light: { label: 'Claro', icon: 'sun' },
  dark: { label: 'Escuro', icon: 'moon' },
} as const

let theme_media_query: MediaQueryList | null = null
let preference: ThemePreference = 'system'
let initialized = false

function normalize_preference(value: string | null): ThemePreference {
  return value === 'light' || value === 'dark' ? value : 'system'
}

function apply_theme(): void {
  const is_dark = preference === 'system' ? !!theme_media_query?.matches : preference === 'dark'
  const theme = is_dark ? 'dark' : 'light'
  const root = document.documentElement
  root.setAttribute('data-bs-theme', theme)
  root.style.colorScheme = theme
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) {
    const theme_color = getComputedStyle(root)
      .getPropertyValue(is_dark ? '--app-theme-color-dark' : '--app-theme-color-light')
      .trim()
    meta.setAttribute('content', theme_color)
  }
  if (store.get('is_dark_mode') !== is_dark) store.set('is_dark_mode', is_dark)
  update_theme_toggle_button()
}

export function set_theme(theme: string | null = null): void {
  if (theme !== null && theme !== 'system' && theme !== 'auto' && theme !== 'light' && theme !== 'dark') return
  preference = normalize_preference(theme ?? browser_local_storage.getItem(storage_key))
  if (theme !== null) {
    if (preference === 'system') browser_local_storage.removeItem(storage_key)
    else browser_local_storage.setItem(storage_key, preference)
  }
  apply_theme()
}

function close_menu(menu: HTMLDetailsElement, restore_focus = false): void {
  menu.open = false
  if (restore_focus) menu.querySelector<HTMLElement>('summary')?.focus()
}

function choose_theme(event: Event): void {
  const target = event.target instanceof Element ? event.target : null
  const option = target?.closest<HTMLInputElement>('[data-theme-option]')
  if (!option) return
  set_theme(option.value)
  const menu = option.closest<HTMLDetailsElement>('.theme-menu')
  // O rádio continua aberto para percorrer as opções com as setas.
  if (menu && event instanceof MouseEvent && event.type === 'click' && event.detail > 0) close_menu(menu, true)
}

function close_outside(event: Event): void {
  const target = event.target instanceof Element ? event.target : null
  const inside = target?.closest('.theme-menu')
  document.querySelectorAll<HTMLDetailsElement>('.theme-menu[open]').forEach(menu => {
    if (menu !== inside) close_menu(menu)
  })
}

export function init_theme_detection(): void {
  if (initialized) return
  initialized = true
  theme_media_query = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null
  const follow_system = () => {
    if (preference === 'system') apply_theme()
  }
  if (theme_media_query?.addEventListener) theme_media_query.addEventListener('change', follow_system)
  else theme_media_query?.addListener(follow_system)
  window.addEventListener('storage', event => {
    if (!is_local_storage_event(event)) return
    if (event.key === storage_key || event.key === null) {
      preference = normalize_preference(event.newValue)
      apply_theme()
    }
  })
  window.addEventListener('pageshow', follow_system)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) follow_system()
  })
  document.addEventListener('click', choose_theme)
  document.addEventListener('change', choose_theme)
  document.addEventListener('click', close_outside)
  document.addEventListener('focusin', close_outside)
  document.addEventListener('keydown', event => {
    const target = event.target instanceof Element ? event.target : null
    const menu = target?.closest<HTMLDetailsElement>('.theme-menu')
    if (!menu) return
    if (event.key === 'Escape') {
      event.preventDefault()
      close_menu(menu, true)
    } else if (target?.id === 'theme-toggle-btn' && event.key === 'ArrowDown') {
      event.preventDefault()
      menu.open = true
      menu.querySelector<HTMLInputElement>('[data-theme-option]:checked')?.focus()
    }
  })
  set_theme()
}

// Mantém o contrato público já usado pelo shell. Abrir não fixa o tema.
export function toggle_theme(): void {
  const menu = document.querySelector<HTMLDetailsElement>('.theme-menu')
  if (menu) menu.open = !menu.open
}

function button_label(): string {
  return preference === 'system' ? 'Tema automático: acompanha o sistema'
    : preference === 'dark' ? 'Tema escuro fixado' : 'Tema claro fixado'
}

function button_icon(): string {
  const icon = options[preference].icon
  return `<i class="fas fa-${icon}" aria-hidden="true"></i>${
    preference === 'system' ? '' : '<i class="fas fa-pin-angle-fill theme-fixed" aria-hidden="true"></i>'
  }`
}

export function render_theme_menu(): string {
  return `<details class="theme-menu">
    <summary id="theme-toggle-btn" class="btn btn-link nav-link px-2" title="${button_label()}" aria-label="${button_label()}">
      ${button_icon()}
    </summary>
    <fieldset class="theme-options" aria-label="Tema">
      ${Object.entries(options).map(([value, option]) => `<label>
        <input type="radio" name="theme" value="${value}" data-theme-option ${value === preference ? 'checked' : ''}>
        <i class="fas fa-${option.icon}" aria-hidden="true"></i><span>${option.label}</span>
      </label>`).join('')}
    </fieldset>
  </details>`
}

export function update_theme_toggle_button(): void {
  const btn = document.getElementById('theme-toggle-btn')
  if (!btn) return
  btn.innerHTML = button_icon()
  btn.title = button_label()
  btn.setAttribute('aria-label', button_label())
  document.querySelectorAll<HTMLInputElement>('[data-theme-option]').forEach(option => {
    option.checked = option.value === preference
  })
}
